import type { IObjectSchema, Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import type { IAssets } from './assets';
import type { IDiagnosisAddEditValidated } from './diagnosis';
import type { ISchemaContainer } from './schemaContainer';
import { ConclusieDiagnoseOption } from '../enums/conclusieDiagnoseOption';
import { DiagnosticReportValidationTypes } from '../enums/diagnosticReportValidationTypes';
import { ReportCategories } from '../enums/reportCategories';
import { assets } from './assets';
import {
    tcchV1DraftDiagnosticReport,
    tcchV1FinalDiagnosticReport,
    tcciV1DraftDiagnosticReport,
    tcciV1FinalDiagnosticReport,
    tccrV1DraftDiagnosticReport,
    tccrV1FinalDiagnosticReport,
    tcohV1DraftDiagnosticReport,
    tcohV1FinalDiagnosticReport,
    tcohV2DraftDiagnosticReport,
    tcohV2FinalDiagnosticReport,
    tcorV1DraftDiagnosticReport,
    tcorV1FinalDiagnosticReport,
    tiecgV1DraftDiagnosticReport,
    tiecgV1FinalDiagnosticReport,
} from './customControls/cardiology/holterConsult/cardiology';
import {
    dermaCscV1DraftDiagnosticReport,
    dermaCscV1FinalDiagnosticReport,
    dermaCV1DraftDiagnosticReport,
    dermaCV1FinalDiagnosticReport,
    dermaCwondV1DraftDiagnosticReport,
    dermaCwondV1FinalDiagnosticReport,
    dermaOthV1DraftDiagnosticReport,
    dermaOthV1FinalDiagnosticReport,
    dermaSnelCscV1DraftDiagnosticReport,
    dermaSnelCscV1FinalDiagnosticReport,
    dermaSnelCV1DraftDiagnosticReport,
    dermaSnelCV1FinalDiagnosticReport,
} from './customControls/dermatology';
import {
    gtcV1DraftDiagnosticReport,
    gtcV1FinalDiagnosticReport,
    gtoV1DraftDiagnosticReport,
    gtoV1FinalDiagnosticReport,
} from './customControls/general';
import {
    ggzcgV1DraftDiagnosticReport,
    ggzcgV1FinalDiagnosticReport,
    ggzctriageV1DraftDiagnosticReport,
    ggzctriageV1FinalDiagnosticReport,
    ggzV1DischargeReport,
    ggzV1IntakeReport,
} from './customControls/mentalHealth';
import {
    orthCV1DraftDiagnosticReport,
    orthCV1FinalDiagnosticReport,
    orthOFolV1DraftDiagnosticReport,
    orthOFolV1FinalDiagnosticReport,
    orthOV1DraftDiagnosticReport,
    orthOV1FinalDiagnosticReport,
    tfcV1DraftDiagnosticReport,
    tfcV1FinalDiagnosticReport,
    tfoV1DraftDiagnosticReport,
    tfoV1FinalDiagnosticReport,
    tocV1DraftDiagnosticReport,
    tocV1FinalDiagnosticReport,
    tooV1DraftDiagnosticReport,
    tooV1FinalDiagnosticReport,
    tovV1DischargeReport,
    tovV1IntakeReport,
} from './customControls/ophthalmology';
import { ocrkV1DraftDiagnosticReport, ocrkV1FinalDiagnosticReport } from './customControls/ophthalmology/ocrk';
import { oorkV1DraftDiagnosticReport, oorkV1FinalDiagnosticReport } from './customControls/ophthalmology/oork';
import {
    pilotConsultationV1FinalDiagnosticReport,
    pilotExaminationV1FinalDiagnosticReport,
    pilotV1DischargeReport,
    pilotV1IntakeReport,
} from './customControls/pilot';
import {
    tpcV1DraftDiagnosticReport,
    tpcV1FinalDiagnosticReport,
    tpoV1DraftDiagnosticReport,
    tpoV1FinalDiagnosticReport,
} from './customControls/pulmonology';
import {
    osasc1V1DraftDiagnosticReport,
    osasc1V1FinalDiagnosticReport,
    osasc1V1Remark,
    osasc2V1DraftDiagnosticReport,
    osasc2V1FinalDiagnosticReport,
    osasc2V2DraftDiagnosticReport,
    osasc2V2FinalDiagnosticReport,
    osasoV1DiagnosticReport,
    osasvoV1DiagnosticReport,
} from './customControls/somnology';
import { diagnosisSchema as diagnosisBaseSchema } from './diagnosis';

export interface IDiagnosticReportEditPerformerValidated {
    performerOrganizationId: string;
    performerPractitionerId: string;
}

const diagnosticReportEditPerformer = Koi.object().keys({
    performerOrganizationId: Koi.string().uuid(),
    performerPractitionerId: Koi.string().uuid(),
});

export const diagnosticReportEditPerformerSchema: ISchemaContainer<IDiagnosticReportEditPerformerValidated> = {
    schema: diagnosticReportEditPerformer.options({ convert: false, presence: 'required' }),
};

export interface IDiagnosticReportUpdateClarificationRequesterValidated {
    clarificationQARequester: string;
}

const diagnosticReportUpdateClarificationRequester = Koi.object().keys({
    clarificationQARequester: Koi.string().max(10000),
});

// eslint-disable-next-line @stylistic/max-len
export const diagnosticReportUpdateClarificationRequesterSchema: ISchemaContainer<IDiagnosticReportUpdateClarificationRequesterValidated> =
    {
        schema: diagnosticReportUpdateClarificationRequester.options({ convert: false, presence: 'required' }),
    };

export interface IDiagnosticReportUpdateClarificationPerformerValidated {
    clarificationQAPerformer: string;
}

const diagnosticReportUpdateClarificationPerformer = Koi.object().keys({
    clarificationQAPerformer: Koi.string().max(10000),
});

// eslint-disable-next-line @stylistic/max-len
export const diagnosticReportUpdateClarificationPerformerSchema: ISchemaContainer<IDiagnosticReportUpdateClarificationPerformerValidated> =
    {
        schema: diagnosticReportUpdateClarificationPerformer.options({ convert: false, presence: 'required' }),
    };

export interface IDiagnosticReportValidated {
    category: ReportCategories;
    customControlData: any;
    diagnosisRemarks: string | null;
    id?: string;
    patientId: string;
    remark: string | null;
    requestId: string;
}

export const diagnosticReportBase = Koi.object({
    diagnosisRemarks: Koi.string().max(1000).allow(null),
    id: Koi.string().uuid().optional(),
    patientId: Koi.string().uuid(),
    requestId: Koi.string().uuid().optional(),
});

const diagnosticReport = diagnosticReportBase.concat(
    Koi.object({
        category: Koi.koi().enum(ReportCategories),
    })
);

export interface ISaveAndSaveAndSendValidated {
    assets: IAssets;
    diagnosis: IDiagnosisAddEditValidated;
    diagnosticReport: IDiagnosticReportValidated;
    validationType: DiagnosticReportValidationTypes;
}

export function getSaveAndSaveAndSendValidationSchema(
    diagnosticReportValidationType: DiagnosticReportValidationTypes
): ISchemaContainer<ISaveAndSaveAndSendValidated> {
    return {
        schema: Koi.object({
            assets: assets,
            diagnosis: getDiagnosisConsiderationIcd10Schema(diagnosticReportValidationType),
            diagnosticReport: getDiagnosticReportSchema(diagnosticReportValidationType),
            validationType: Koi.koi().enum(DiagnosticReportValidationTypes).disallow(null),
        }).options({ convert: false, presence: 'required' }),
    };
}

export function getDiagnosticReportValidationSchema(
    diagnosticReportValidationType: DiagnosticReportValidationTypes
): ISchemaContainer<IDiagnosticReportValidated> {
    return {
        schema: getDiagnosticReportSchema(diagnosticReportValidationType).options({
            convert: false,
            presence: 'required',
        }),
    };
}

export function getDiagnosticReportSchema(diagnosticReportValidationType: DiagnosticReportValidationTypes) {
    const schemas = getDiagnosticReportSchemas(diagnosticReportValidationType);

    if (!schemas.customControlSchema) {
        return diagnosticReport
            .concat(
                Koi.object({
                    customControlData: Koi.valid(null),
                })
            )
            .concat(schemas.baseSchemaExistention);
    }

    return diagnosticReport
        .concat(
            Koi.object({
                customControlData: schemas.customControlSchema.disallow(null),
            })
        )
        .concat(schemas.baseSchemaExistention);
}

export function getDiagnosticReportSchemas(diagnosticReportValidationType: DiagnosticReportValidationTypes) {
    let customControlSchema: IObjectSchema | Joi.AlternativesSchema;

    switch (diagnosticReportValidationType) {
        case DiagnosticReportValidationTypes.tpcV1AddDraftDiagnosticReport:
            customControlSchema = tpcV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpcV1AddFinalDiagnosticReport:
            customControlSchema = tpcV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpcV1EditDraftDiagnosticReport:
            customControlSchema = tpcV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpcV1EditFinalDiagnosticReport:
            customControlSchema = tpcV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tpoV1AddDraftDiagnosticReport:
            customControlSchema = tpoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpoV1AddFinalDiagnosticReport:
            customControlSchema = tpoV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpoV1EditDraftDiagnosticReport:
            customControlSchema = tpoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpoV1EditFinalDiagnosticReport:
            customControlSchema = tpoV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpoV1AddDraftDiagnosticReportWithRequest:
            customControlSchema = tpoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tpoV1AddFinalDiagnosticReportWithRequest:
            customControlSchema = tpoV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV1AddDraftDiagnosticReport:
            customControlSchema = tcohV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV1AddFinalDiagnosticReport:
            customControlSchema = tcohV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV1EditDraftDiagnosticReport:
            customControlSchema = tcohV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV1EditFinalDiagnosticReport:
            customControlSchema = tcohV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV2AddDraftDiagnosticReport:
            customControlSchema = tcohV2DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV2AddFinalDiagnosticReport:
            customControlSchema = tcohV2FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV2EditDraftDiagnosticReport:
            customControlSchema = tcohV2DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcohV2EditFinalDiagnosticReport:
            customControlSchema = tcohV2FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tcorV1AddDraftDiagnosticReport:
            customControlSchema = tcorV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcorV1AddFinalDiagnosticReport:
            customControlSchema = tcorV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcorV1AddDraftDiagnosticReportWithRequest:
            customControlSchema = tcorV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcorV1AddFinalDiagnosticReportWithRequest:
            customControlSchema = tcorV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcorV1EditDraftDiagnosticReport:
            customControlSchema = tcorV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcorV1EditFinalDiagnosticReport:
            customControlSchema = tcorV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tccrV1AddDraftDiagnosticReport:
            customControlSchema = tccrV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tccrV1AddFinalDiagnosticReport:
            customControlSchema = tccrV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tccrV1EditDraftDiagnosticReport:
            customControlSchema = tccrV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tccrV1EditFinalDiagnosticReport:
            customControlSchema = tccrV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tcchV1AddDraftDiagnosticReport:
            customControlSchema = tcchV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcchV1AddFinalDiagnosticReport:
            customControlSchema = tcchV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcchV1EditDraftDiagnosticReport:
            customControlSchema = tcchV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcchV1EditFinalDiagnosticReport:
            customControlSchema = tcchV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tcciV1AddDraftDiagnosticReport:
            customControlSchema = tcciV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcciV1AddFinalDiagnosticReport:
            customControlSchema = tcciV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcciV1EditDraftDiagnosticReport:
            customControlSchema = tcciV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tcciV1EditFinalDiagnosticReport:
            customControlSchema = tcciV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tiecgV1AddDraftDiagnosticReport:
            customControlSchema = tiecgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tiecgV1AddFinalDiagnosticReport:
            customControlSchema = tiecgV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tiecgV1EditDraftDiagnosticReport:
            customControlSchema = tiecgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tiecgV1EditFinalDiagnosticReport:
            customControlSchema = tiecgV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tiecgV1AddDraftDiagnosticReportWithRequest:
            customControlSchema = tiecgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tiecgV1AddFinalDiagnosticReportWithRequest:
            customControlSchema = tiecgV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.osasc1V1AddDraftDiagnosticReport:
            customControlSchema = osasc1V1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc1V1AddFinalDiagnosticReport:
            customControlSchema = osasc1V1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc1V1EditDraftDiagnosticReport:
            customControlSchema = osasc1V1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc1V1EditFinalDiagnosticReport:
            customControlSchema = osasc1V1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.osasoV1AddDraftDiagnosticReport:
            customControlSchema = osasoV1DiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasoV1AddFinalDiagnosticReport:
            customControlSchema = osasoV1DiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasoV1EditDraftDiagnosticReport:
            customControlSchema = osasoV1DiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasoV1EditFinalDiagnosticReport:
            customControlSchema = osasoV1DiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.osasvoV1AddDraftDiagnosticReport:
            customControlSchema = osasvoV1DiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasvoV1AddFinalDiagnosticReport:
            customControlSchema = osasvoV1DiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasvoV1EditDraftDiagnosticReport:
            customControlSchema = osasvoV1DiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasvoV1EditFinalDiagnosticReport:
            customControlSchema = osasvoV1DiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.osasc2V1AddDraftDiagnosticReport:
            customControlSchema = osasc2V1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc2V1AddFinalDiagnosticReport:
            customControlSchema = osasc2V1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc2V1EditDraftDiagnosticReport:
            customControlSchema = osasc2V1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc2V1EditFinalDiagnosticReport:
            customControlSchema = osasc2V1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.osasc2V2AddDraftDiagnosticReport:
            customControlSchema = osasc2V2DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc2V2AddFinalDiagnosticReport:
            customControlSchema = osasc2V2FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc2V2EditDraftDiagnosticReport:
            customControlSchema = osasc2V2DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.osasc2V2EditFinalDiagnosticReport:
            customControlSchema = osasc2V2FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tfoV1AddDraftDiagnosticReport:
            customControlSchema = tfoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfoV1AddFinalDiagnosticReport:
            customControlSchema = tfoV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfoV1EditDraftDiagnosticReport:
            customControlSchema = tfoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfoV1EditFinalDiagnosticReport:
            customControlSchema = tfoV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfoV1AddDraftDiagnosticReportWithRequest:
            customControlSchema = tfoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfoV1AddFinalDiagnosticReportWithRequest:
            customControlSchema = tfoV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tocV1AddDraftDiagnosticReport:
            customControlSchema = tocV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tocV1AddFinalDiagnosticReport:
            customControlSchema = tocV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tocV1EditDraftDiagnosticReport:
            customControlSchema = tocV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tocV1EditFinalDiagnosticReport:
            customControlSchema = tocV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.tooV1AddDraftDiagnosticReport:
            customControlSchema = tooV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tooV1AddFinalDiagnosticReport:
            customControlSchema = tooV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tooV1EditDraftDiagnosticReport:
            customControlSchema = tooV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tooV1EditFinalDiagnosticReport:
            customControlSchema = tooV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tooV1AddDraftDiagnosticReportWithRequest:
            customControlSchema = tooV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tooV1AddFinalDiagnosticReportWithRequest:
            customControlSchema = tooV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.pilotExaminationV1AddDraftDiagnosticReport:
            customControlSchema = pilotExaminationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotExaminationV1AddFinalDiagnosticReport:
            customControlSchema = pilotExaminationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotExaminationV1EditDraftDiagnosticReport:
            customControlSchema = pilotExaminationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotExaminationV1EditFinalDiagnosticReport:
            customControlSchema = pilotExaminationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotExaminationV1AddDraftDiagnosticReportWithRequest:
            customControlSchema = pilotExaminationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotExaminationV1AddFinalDiagnosticReportWithRequest:
            customControlSchema = pilotExaminationV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.pilotConsultationV1AddDraftDiagnosticReport:
            customControlSchema = pilotConsultationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotConsultationV1AddFinalDiagnosticReport:
            customControlSchema = pilotConsultationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotConsultationV1EditDraftDiagnosticReport:
            customControlSchema = pilotConsultationV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.pilotConsultationV1EditFinalDiagnosticReport:
            customControlSchema = pilotConsultationV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.pilotReferralV1AddDraftIntakeDiagnosticReport:
            customControlSchema = pilotV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.pilotReferralV1AddFinalIntakeDiagnosticReport:
            customControlSchema = pilotV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.pilotReferralV1EditDraftIntakeDiagnosticReport:
            customControlSchema = pilotV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.pilotReferralV1EditFinalIntakeDiagnosticReport:
            customControlSchema = pilotV1IntakeReport;
            break;

        case DiagnosticReportValidationTypes.pilotReferralV1AddDraftDischargeDiagnosticReport:
            customControlSchema = pilotV1DischargeReport;
            break;
        case DiagnosticReportValidationTypes.pilotReferralV1AddFinalDischargeDiagnosticReport:
            customControlSchema = pilotV1DischargeReport;
            break;
        case DiagnosticReportValidationTypes.pilotReferralV1EditDraftDischargeDiagnosticReport:
            customControlSchema = pilotV1DischargeReport;
            break;
        case DiagnosticReportValidationTypes.pilotReferralV1EditFinalDischargeDiagnosticReport:
            customControlSchema = pilotV1DischargeReport;
            break;

        case DiagnosticReportValidationTypes.tfcV1AddDraftDiagnosticReport:
            customControlSchema = tfcV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfcV1AddFinalDiagnosticReport:
            customControlSchema = tfcV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfcV1EditDraftDiagnosticReport:
            customControlSchema = tfcV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.tfcV1EditFinalDiagnosticReport:
            customControlSchema = tfcV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.ggzctV1AddDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzctV1AddFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzctV1EditDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzctV1EditFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.ggzcf2fV1AddDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcf2fV1AddFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcf2fV1EditDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcf2fV1EditFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.ggzcgV1AddDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcgV1EditDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcgV1AddFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcgV1EditFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.ggzcmdoV1AddDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcmdoV1EditDraftDiagnosticReport:
            customControlSchema = ggzcgV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcmdoV1AddFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzcmdoV1EditFinalDiagnosticReport:
            customControlSchema = ggzcgV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.ggzctriageV1AddDraftDiagnosticReport:
            customControlSchema = ggzctriageV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzctriageV1AddFinalDiagnosticReport:
            customControlSchema = ggzctriageV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzctriageV1EditDraftDiagnosticReport:
            customControlSchema = ggzctriageV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ggzctriageV1EditFinalDiagnosticReport:
            customControlSchema = ggzctriageV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.ggzV1AddIntakeDiagnosticReport:
            customControlSchema = ggzV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.ggzV1EditIntakeDiagnosticReport:
            customControlSchema = ggzV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.ggzV1AddDischargeDiagnosticReport:
            customControlSchema = ggzV1DischargeReport;
            break;
        case DiagnosticReportValidationTypes.ggzV1EditDischargeDiagnosticReport:
            customControlSchema = ggzV1DischargeReport;
            break;

        case DiagnosticReportValidationTypes.tovV1AddIntakeDiagnosticReport:
            customControlSchema = tovV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.tovV1EditIntakeDiagnosticReport:
            customControlSchema = tovV1IntakeReport;
            break;
        case DiagnosticReportValidationTypes.tovV1AddDischargeDiagnosticReport:
            customControlSchema = tovV1DischargeReport;
            break;
        case DiagnosticReportValidationTypes.tovV1EditDischargeDiagnosticReport:
            customControlSchema = tovV1DischargeReport;
            break;

        case DiagnosticReportValidationTypes.gtoV1AddDraftDiagnosticReport:
            customControlSchema = gtoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.gtoV1EditDraftDiagnosticReport:
            customControlSchema = gtoV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.gtoV1AddFinalDiagnosticReport:
            customControlSchema = gtoV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.gtoV1EditFinalDiagnosticReport:
            customControlSchema = gtoV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.gtcV1AddDraftDiagnosticReport:
            customControlSchema = gtcV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.gtcV1AddFinalDiagnosticReport:
            customControlSchema = gtcV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.gtcV1EditDraftDiagnosticReport:
            customControlSchema = gtcV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.gtcV1EditFinalDiagnosticReport:
            customControlSchema = gtcV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.dermaCV1AddDraftDiagnosticReport:
            customControlSchema = dermaCV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCV1AddFinalDiagnosticReport:
            customControlSchema = dermaCV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCV1EditDraftDiagnosticReport:
            customControlSchema = dermaCV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCV1EditFinalDiagnosticReport:
            customControlSchema = dermaCV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.dermaSnelCV1AddDraftDiagnosticReport:
            customControlSchema = dermaSnelCV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaSnelCV1AddFinalDiagnosticReport:
            customControlSchema = dermaSnelCV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaSnelCV1EditDraftDiagnosticReport:
            customControlSchema = dermaSnelCV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaSnelCV1EditFinalDiagnosticReport:
            customControlSchema = dermaSnelCV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.dermaCscV1AddDraftDiagnosticReport:
            customControlSchema = dermaCscV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCscV1AddFinalDiagnosticReport:
            customControlSchema = dermaCscV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCscV1EditDraftDiagnosticReport:
            customControlSchema = dermaCscV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCscV1EditFinalDiagnosticReport:
            customControlSchema = dermaCscV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.dermaSnelCscV1AddDraftDiagnosticReport:
            customControlSchema = dermaSnelCscV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaSnelCscV1AddFinalDiagnosticReport:
            customControlSchema = dermaSnelCscV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaSnelCscV1EditDraftDiagnosticReport:
            customControlSchema = dermaSnelCscV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaSnelCscV1EditFinalDiagnosticReport:
            customControlSchema = dermaSnelCscV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.dermaCwondV1AddDraftDiagnosticReport:
            customControlSchema = dermaCwondV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCwondV1AddFinalDiagnosticReport:
            customControlSchema = dermaCwondV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCwondV1EditDraftDiagnosticReport:
            customControlSchema = dermaCwondV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaCwondV1EditFinalDiagnosticReport:
            customControlSchema = dermaCwondV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.dermaOthV1AddDraftDiagnosticReport:
            customControlSchema = dermaOthV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaOthV1EditDraftDiagnosticReport:
            customControlSchema = dermaOthV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaOthV1AddFinalDiagnosticReport:
            customControlSchema = dermaOthV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.dermaOthV1EditFinalDiagnosticReport:
            customControlSchema = dermaOthV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.orthOV1AddDraftDiagnosticReport:
            customControlSchema = orthOV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOV1AddFinalDiagnosticReport:
            customControlSchema = orthOV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOV1EditDraftDiagnosticReport:
            customControlSchema = orthOV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOV1EditFinalDiagnosticReport:
            customControlSchema = orthOV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOFolV1AddDraftDiagnosticReport:
            customControlSchema = orthOFolV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOFolV1AddFinalDiagnosticReport:
            customControlSchema = orthOFolV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOFolV1EditDraftDiagnosticReport:
            customControlSchema = orthOFolV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthOFolV1EditFinalDiagnosticReport:
            customControlSchema = orthOFolV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.orthCV1AddDraftDiagnosticReport:
            customControlSchema = orthCV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthCV1AddFinalDiagnosticReport:
            customControlSchema = orthCV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthCV1EditDraftDiagnosticReport:
            customControlSchema = orthCV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.orthCV1EditFinalDiagnosticReport:
            customControlSchema = orthCV1FinalDiagnosticReport;
            break;

        case DiagnosticReportValidationTypes.oorkV1EditDraftDiagnosticReport:
        case DiagnosticReportValidationTypes.oorkV1AddDraftDiagnosticReport:
            customControlSchema = oorkV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.oorkV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.oorkV1AddFinalDiagnosticReport:
            customControlSchema = oorkV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ocrkV1AddDraftDiagnosticReport:
            customControlSchema = ocrkV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ocrkV1AddFinalDiagnosticReport:
            customControlSchema = ocrkV1FinalDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ocrkV1EditDraftDiagnosticReport:
            customControlSchema = ocrkV1DraftDiagnosticReport;
            break;
        case DiagnosticReportValidationTypes.ocrkV1EditFinalDiagnosticReport:
            customControlSchema = ocrkV1FinalDiagnosticReport;
            break;

        default:
            throw new Error(`Unable to find diagnostic report validation schema, ${diagnosticReportValidationType}`);
    }

    const baseSchemaExistention = getRemarkSchema(diagnosticReportValidationType);
    const diagnosisSchema = getDiagnosisConsiderationIcd10Schema(diagnosticReportValidationType);

    return { baseSchemaExistention, customControlSchema, diagnosisSchema };
}

function getDiagnosisConsiderationIcd10Schema(diagnosticReportValidationType: DiagnosticReportValidationTypes) {
    let additionalRestrictions;
    switch (diagnosticReportValidationType) {
        case DiagnosticReportValidationTypes.tpcV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tpcV1EditFinalDiagnosticReport:
            additionalRestrictions = {
                diagnosisConsiderationIcd10: Koi.when(
                    Koi.ref('/diagnosticReport.customControlData.conclusie_diagnose'),
                    {
                        is: ConclusieDiagnoseOption.ja,
                        otherwise: Koi.string().max(255).allow(null),
                        then: Koi.when(Koi.ref('/diagnosticReport.customControlData.diagnose_COPD_value'), {
                            is: Koi.not(ConclusieDiagnoseOption.ja).disallow(null),
                            otherwise: Koi.string().max(255).allow(null),
                            then: Koi.when(Koi.ref('/diagnosticReport.customControlData.diagnose_astma_value'), {
                                is: Koi.not(ConclusieDiagnoseOption.ja).disallow(null),
                                otherwise: Koi.string().max(255).allow(null),
                                then: Koi.string().max(255).disallow(null),
                            }),
                        }),
                    }
                ),
            };
            break;
        case DiagnosticReportValidationTypes.gtcV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.gtcV1EditFinalDiagnosticReport:
            additionalRestrictions = {
                diagnosisConsiderationIcd10: Koi.string().max(255),
            };
            break;
        case DiagnosticReportValidationTypes.tccrV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tccrV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tcchV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tcchV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tcciV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tcciV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tfcV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.tfcV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzcgV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzcgV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzcf2fV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzcf2fV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzcmdoV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzcmdoV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzctV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.ggzctV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.dermaCV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.dermaCV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.dermaSnelCV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.dermaSnelCV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.dermaCwondV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.dermaCwondV1EditFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.orthCV1AddFinalDiagnosticReport:
        case DiagnosticReportValidationTypes.orthCV1EditFinalDiagnosticReport:
            additionalRestrictions = {
                diagnosisConsiderationIcd10: Koi.when(
                    Koi.ref('/diagnosticReport.customControlData.conclusie_diagnose'),
                    {
                        is: ConclusieDiagnoseOption.ja,
                        otherwise: Koi.string().max(255).allow(null),
                        then: Koi.string().max(255),
                    }
                ),
            };
            break;
        default:
            additionalRestrictions = { diagnosisConsiderationIcd10: Koi.string().max(255).allow(null) };
    }

    return diagnosisBaseSchema.keys(additionalRestrictions);
}

function getRemarkSchema(diagnosticReportValidationType: DiagnosticReportValidationTypes) {
    switch (diagnosticReportValidationType) {
        case DiagnosticReportValidationTypes.osasc1V1AddFinalDiagnosticReport:
            return Koi.object({ remark: osasc1V1Remark });
        case DiagnosticReportValidationTypes.osasc1V1EditFinalDiagnosticReport:
            return Koi.object({ remark: osasc1V1Remark });
        default:
            return Koi.object({ remark: Koi.string().required().max(10000).allow(null) });
    }
}
